import { useState } from "react";
import { setLayoutMode } from "../LayoutSlice_Store";
import { openDocumentPreview } from "../DocumentPreviewSlice_Store";
import { buildHighlightRequest, buildDocumentPreviewPayload } from "../services/factPreviewService";
import { canPreviewDocument } from "../services/documentPreviewService";
import { selectDocumentFromRegistry } from "../ResultsSlice_Store";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import NonPreviewableDocumentModal from "./NonPreviewableDocumentModal";

/**
 * Document color scheme interface
 */
interface DocumentColorScheme {
  bg: string;
  text: string;
  border: string;
}

/**
 * Fact interface matching the structure from ResultsSlice
 */
interface Fact {
  factId: string;
  documentId: string;
  rationale?: string;
  highlightContentString?: string;
  factSourceType?: 'text' | 'image' | 'tabular';
  text?: string;
  docDisplayLabel?: string;
  [key: string]: unknown;
}


/**
 * Component props interface
 */
interface InlineFactBadgeProps {
  factId: string;
  fact: Fact | null;
  displayIndex: number | null;
  documentDisplayIndex?: number;
  allFacts?: Fact[];
  factIdToIndex?: Record<string, number>;
  loading?: boolean;
}

/**
 * Document color palette for fact badges
 * Uses CVC secondary colors for visual distinction across documents
 */
const DOCUMENT_COLORS: DocumentColorScheme[] = [
  { bg: 'bg-cvc-lb2', text: 'text-cvc-blue', border: 'border-cvc-lb1' },
  { bg: 'bg-emerald-50', text: 'text-cvc-green', border: 'border-emerald-200' },
  { bg: 'bg-cyan-50', text: 'text-cvc-marine', border: 'border-cyan-200' },
  { bg: 'bg-purple-50', text: 'text-cvc-purple', border: 'border-purple-200' },
  { bg: 'bg-red-50', text: 'text-cvc-coral', border: 'border-red-200' },
  { bg: 'bg-amber-50', text: 'text-cvc-yellow', border: 'border-amber-200' },
];

/**
 * Get color scheme for a document based on its display index
 */
function getDocumentColor(documentDisplayIndex: number): DocumentColorScheme {
  if (!documentDisplayIndex || documentDisplayIndex < 1) {
    return DOCUMENT_COLORS[0];
  }
  const index = (documentDisplayIndex - 1) % DOCUMENT_COLORS.length;
  return DOCUMENT_COLORS[index];
}

/**
 * InlineFactBadge - renders a numbered fact badge that opens the document preview on click.
 *
 * Phase 4: This component accepts fact data and passes ALL facts for the document to the preview.
 */
export default function InlineFactBadge({
  factId,
  fact,
  displayIndex,
  documentDisplayIndex,
  allFacts = [],
  factIdToIndex,
  loading = false
}: InlineFactBadgeProps) {
  const dispatch = useAppDispatch();
  const [showNonPreviewableModal, setShowNonPreviewableModal] = useState(false);

  // Get full document from registry (merged across all turns for citation resolution)
  // Core-agent sends facts with 'documentId' field that maps to 'docId' in results
  const fullDocument = useAppSelector(state => {
    if (!fact?.documentId) return null;
    return selectDocumentFromRegistry(state, fact.documentId);
  });

  // Handle click to open document preview with fact highlighting
  const handleClick = () => {
    if (loading) return;

    const documentToUse = buildDocumentPreviewPayload(fullDocument, fact);

    if (!fact || !documentToUse || !canPreviewDocument(documentToUse)) {
      setShowNonPreviewableModal(true);
      return;
    }

    // Gather ALL facts for this document for multi-citation view
    const documentFacts = allFacts.filter(f => f.documentId === fact.documentId);
    const clickedFactIndex = documentFacts.findIndex(f => f.factId === fact.factId);

    const highlightRequest = buildHighlightRequest(
      { documentId: fact.documentId, facts: documentFacts },
      clickedFactIndex
    );

    if (highlightRequest) {
      highlightRequest.fact = fact;
      highlightRequest.factId = fact.factId;
      highlightRequest.allDocumentFacts = documentFacts;
      highlightRequest.factSourceType = fact.factSourceType || null;
      highlightRequest.factIdToIndex = factIdToIndex;
    }

    dispatch(setLayoutMode('workspace'));
    dispatch(openDocumentPreview({ document: documentToUse, highlightRequest }));
  };

  // Show loading state if requested OR if we don't have display index yet
  if (loading || !displayIndex) {
    return (
      <span
        className="inline-flex items-center justify-center w-5 h-5 ml-0.5 mr-0.5 bg-cvc-lb2 border border-cvc-lb1 rounded-full align-middle transition-all duration-300"
        title="Loading fact..."
        aria-label="Loading fact"
      >
        <svg className="w-3 h-3 text-cvc-blue animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </span>
    );
  }

  // Determine document display index for coloring
  const docIndex = documentDisplayIndex || 1;
  const colors = getDocumentColor(docIndex);

  const sourceLabel =
    (!fact || !fact.factSourceType)
      ? null
      : fact.factSourceType === 'image'
        ? 'Image fact'
        : 'Text fact';

  // Build tooltip text with fact rationale
  const tooltipParts = [];
  tooltipParts.push(`Fact ${displayIndex}`);
  if (sourceLabel) {
    tooltipParts.push(`• ${sourceLabel}`);
  }
  if (fact?.rationale) {
    tooltipParts.push(`• ${fact.rationale}`);
  }
  if (fullDocument) {
    tooltipParts.push(`• ${fullDocument.documentName || fact?.docDisplayLabel || 'document'}`);
  }
  const tooltipText = tooltipParts.join(' ');

  return (
    <>
      <span
        onClick={handleClick}
        onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        className={`inline-flex items-center justify-center ${displayIndex >= 100 ? 'min-w-6 px-1' : 'w-5'} h-5 ml-0.5 mr-0.5 text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border} rounded-full hover:scale-110 transition-all duration-300 ease-in-out cursor-pointer align-middle`}
        title={tooltipText}
        aria-label={tooltipText}
      >
        {displayIndex}
      </span>
      <NonPreviewableDocumentModal
        isOpen={showNonPreviewableModal}
        onClose={() => setShowNonPreviewableModal(false)}
        documentName={fullDocument?.originalFilename || fullDocument?.documentName || fact?.docDisplayLabel || 'Unknown document'}
        filePath={fullDocument?.filePath}
        highlightText={fact?.highlightContentString}
        pageIndex={typeof fact?.pageIndex === 'number' ? fact.pageIndex : undefined}
        factSourceType={fact?.factSourceType}
        sharepointSiteId={fullDocument?.sharepointSiteId}
        sharepointDriveId={fullDocument?.sharepointDriveId}
        sharepointItemId={fullDocument?.sharepointItemId}
      />
    </>
  );
}
